import { skipToken, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import {
  type AutocompleteResult,
  type AutocompleteSearchOptions,
  type SearchSuggestionsResult,
  type SearchType,
} from 'invictus-sdk-typescript'

import { useAutocompleteSdk } from '@Hooks/sdk/useAutocompleteSdk'
import { QUERY_KEY } from '@Types/queryKey'

export type SearchAutocompleteQueryOptions<AutocompleteModelType = AutocompleteResult> = Omit<
  UseQueryOptions<AutocompleteResult, Error, AutocompleteModelType, (string | boolean)[]>,
  'queryKey'
>

const defaultOptions = {
  retry: false,
  staleTime: 5 * 60 * 1000,
}

export const useSearchAutocompleteQuery = <AutocompleteModelType = AutocompleteResult>(
  searchValue: string,
  overriddenOptions?: SearchAutocompleteQueryOptions<AutocompleteModelType>,
  searchOptions: AutocompleteSearchOptions = {},
): UseQueryResult<AutocompleteModelType> => {
  const autocompleteSdk = useAutocompleteSdk()

  const options = { ...defaultOptions, ...overriddenOptions }
  const getAutocompleteItems = async () => autocompleteSdk.autocomplete(searchValue, searchOptions)

  const { location, keepStationsOnly, isInFranceOnly } = searchOptions
  const searchOptionsKey = [
    'location',
    ...(location ? ['latitude', `${location.latitude}`, 'longitude', `${location.longitude}`] : []),
    'keepStationsOnly',
    !!keepStationsOnly,
    'isInFranceOnly',
    !!isInFranceOnly,
  ]

  return useQuery({
    queryKey: [QUERY_KEY.searchAutocomplete, searchValue, ...searchOptionsKey],
    queryFn: getAutocompleteItems,
    ...options,
  })
}

type SearchAutocompleteSuggestionsQueryOptions = Omit<
  UseQueryOptions<SearchSuggestionsResult, Error, SearchSuggestionsResult, (string | SearchType | undefined)[]>,
  'queryKey'
>

const defaultSuggestionsOptions: SearchAutocompleteSuggestionsQueryOptions = {
  retry: false,
  staleTime: 0,
}

// TODO : discuter de la meilleure façon de gérer le isEnabled https://jira.vsct.fr/browse/SIBR-5635

export const useSearchAutocompleteSuggestionsQuery = (
  searchType: SearchType,
  isEnabled = false,
): UseQueryResult<SearchSuggestionsResult> => {
  const autocompleteSdk = useAutocompleteSdk()

  const getSuggestionsItems = async () => autocompleteSdk.suggestions(searchType)

  return useQuery({
    queryKey: [QUERY_KEY.searchAutocompleteSuggestions, searchType],
    queryFn: isEnabled ? getSuggestionsItems : skipToken,
    ...defaultSuggestionsOptions,
  })
}
